<template>
  <div class="network-background">
    <canvas ref="canvasRef"></canvas>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref } from "vue";

type Point = {
  x: number;
  y: number;
  vx: number;
  vy: number;
};

export default defineComponent({
  name: "NetworkBackground",
  setup() {
    const canvasRef = ref<HTMLCanvasElement | null>(null);
    const points: Point[] = [];
    const mouse = { x: 0, y: 0, active: false };
    const POINTS_COUNT = 50;

    let ctx: CanvasRenderingContext2D | null = null;
    let width = window.innerWidth;
    let height = window.innerHeight;
    let dpr = window.devicePixelRatio || 1;
    let animationId = 0;

    const resetCanvasSize = () => {
      const canvas = canvasRef.value;
      if (!canvas) {
        return;
      }

      width = window.innerWidth;
      height = window.innerHeight;
      dpr = window.devicePixelRatio || 1;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      if (!ctx) {
        ctx = canvas.getContext("2d");
      }
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const initPoints = () => {
      points.length = 0;
      for (let i = 0; i < POINTS_COUNT; i++) {
        points.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
        });
      }
    };

    const drawLines = (
      context: CanvasRenderingContext2D,
      fromPoint: Point,
      toPoint: Point
    ) => {
      const dx = fromPoint.x - toPoint.x;
      const dy = fromPoint.y - toPoint.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 150) {
        context.strokeStyle = `rgba(255, 140, 0, ${1 - dist / 150})`;
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(fromPoint.x, fromPoint.y);
        context.lineTo(toPoint.x, toPoint.y);
        context.stroke();
      }
    };

    const animate = () => {
      const context = ctx;
      if (!context) {
        return;
      }

      context.clearRect(0, 0, width, height);

      for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
          drawLines(context, points[i], points[j]);
        }
      }

      if (mouse.active) {
        points.forEach((point) => {
          const dx = point.x - mouse.x;
          const dy = point.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 180) {
            context.strokeStyle = `rgba(255, 200, 120, ${1 - dist / 180})`;
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(point.x, point.y);
            context.lineTo(mouse.x, mouse.y);
            context.stroke();
          }
        });

        context.fillStyle = "rgba(255, 200, 120, 0.7)";
        context.beginPath();
        context.arc(mouse.x, mouse.y, 3, 0, Math.PI * 2);
        context.fill();
      }

      points.forEach((point) => {
        point.x += point.vx;
        point.y += point.vy;

        if (point.x < 0 || point.x > width) {
          point.vx *= -1;
        }
        if (point.y < 0 || point.y > height) {
          point.vy *= -1;
        }

        context.fillStyle = "orange";
        context.beginPath();
        context.arc(point.x, point.y, 2, 0, Math.PI * 2);
        context.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    const handleResize = () => {
      resetCanvasSize();
      initPoints();
    };

    const handleMouseMove = (event: MouseEvent) => {
      mouse.x = event.clientX;
      mouse.y = event.clientY;
      mouse.active = true;
    };

    const handleMouseLeave = () => {
      mouse.active = false;
    };

    onMounted(() => {
      resetCanvasSize();
      initPoints();
      animate();
      window.addEventListener("resize", handleResize);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseleave", handleMouseLeave);
    });

    onBeforeUnmount(() => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    });

    return {
      canvasRef,
    };
  },
});
</script>

<style scoped>
.network-background {
  position: fixed;
  inset: 0;
  background: #0d0d0d;
  z-index: 0;
  pointer-events: none;
}

.network-background canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
